import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import FullscreenImageDialog from "./FullviewImage";
import ImageCarousel from "./ImageCarousel";
import ProductBasicInfo from "./ProductBasicInfo";
import QuantitySelector from "./QuantitySelector";
import ShippingCalculator from "./ShippingCalculator";
import StoreInfo from "./StoreInfo";
import ProductDescription from "./ProductDescription";
import RelatedProductsCarousel from "./RelatedProductsCarousel";
import ProductVariantSelector from "./ProductVariantSelector";
import { useProductDetail } from "./useProductDetail";
import { ProductWithImages } from "../../types/Product";
import { ProductVariantAttributeWithValues, ProductVariant } from "../../types/ProductVariant";
import { AppConfigFields } from "../../types/appConfigEntities";
import { HttpProduct, HttpCategory } from "../../http";

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface ProductDetailProps {
  productWithImages: ProductWithImages;
  variantAttributes?: ProductVariantAttributeWithValues[];
}

const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] || 1;

export default function ProductDetail({ productWithImages, variantAttributes = [] }: ProductDetailProps) {
  const location = useLocation();
  const productInfo = productWithImages.producto;
  const productImages = useMemo(() => {
    const additional = productWithImages.imagenesAdicionales
      .slice()
      .sort((a, b) => a.posicion - b.posicion)
      .map((img) => img.urlImagen)
      .filter(Boolean);

    return [productInfo.urlImagenPrincipal, ...additional.filter((image) => image !== productInfo.urlImagenPrincipal)].filter(Boolean);
  }, [productWithImages.imagenesAdicionales, productInfo.urlImagenPrincipal]);

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [selectedVariantAttributes, setSelectedVariantAttributes] = useState<{ name: string; value: string }[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(minimumPurchase);

  const {
    selectedImage,
    fullscreenDialogOpen,
    handleCalculateShipping,
    nextImage,
    prevImage,
    setSelectedImage,
    openFullscreenDialog,
    closeFullscreenDialog,
  } = useProductDetail(productInfo, productImages);

  useEffect(() => {
    const variantStock = selectedVariant?.stock ?? productInfo.stock ?? 0;
    const safeMin = Math.min(minimumPurchase, Math.max(variantStock, minimumPurchase));
    setQuantity(safeMin);
  }, [selectedVariant?.id, productInfo.id]);

  const handleQuantityChange = (value: number) => {
    const stock = selectedVariant?.stock ?? productInfo.stock ?? 0;
    const safeValue = Math.min(Math.max(value, minimumPurchase), stock);
    setQuantity(safeValue);
  };

  const buildBreadcrumbs = async (categoryId: string, allCategories: any[], fromCategorySku?: string) => {
    const crumbs: BreadcrumbItem[] = [{ label: "Inicio", href: "/" }];

    if (fromCategorySku) {
      const categoryName = fromCategorySku.split("-").map((word) => word.toUpperCase()).join(" ");
      crumbs.push({ label: categoryName, href: `/categoria/${fromCategorySku}` });
      crumbs.push({ label: productInfo.nombre, href: `/productos/${productInfo.sku}` });
      return crumbs;
    }

    const buildCrumb = (catId: string | null) => {
      if (!catId) return;
      const cat = allCategories.find((c) => c.id === catId);
      if (cat) {
        buildCrumb(cat.idCategoriaPadre);
        crumbs.push({ label: cat.nombre, href: `/categoria/${cat.sku}` });
      }
    };

    buildCrumb(categoryId);
    crumbs.push({ label: productInfo.nombre, href: `/productos/${productInfo.sku}` });
    return crumbs;
  };

  useEffect(() => {
    const fetchBreadcrumbs = async () => {
      const fromCategorySku = location.state?.fromCategorySku as string | undefined;

      if (productInfo.categoriasIds && productInfo.categoriasIds.length > 0) {
        try {
          const allCategories = await HttpCategory.getAllCategories();
          const crumbs = await buildBreadcrumbs(productInfo.categoriasIds[0], allCategories, fromCategorySku);
          setBreadcrumbs(crumbs);
        } catch (error) {
          console.error("Error fetching categories for breadcrumbs:", error);
          setBreadcrumbs([{ label: "Inicio", href: "/" }, { label: productInfo.nombre, href: `/productos/${productInfo.sku}` }]);
        }
      } else {
        setBreadcrumbs([{ label: "Inicio", href: "/" }, { label: productInfo.nombre, href: `/productos/${productInfo.sku}` }]);
      }
    };

    fetchBreadcrumbs();
  }, [productInfo, location.state]);

  useEffect(() => {
    if (!productInfo || !productInfo.idProductoVarianteDefault) return;

    const loadDefaultVariant = async () => {
      try {
        const variantDetail = await HttpProduct.getProductVariantDetail(productInfo.idProductoVarianteDefault);
        setSelectedVariant(variantDetail.productoVariante);
        setSelectedVariantAttributes(
          variantDetail.productoVarianteAtributoValores.map((value) => ({
            name: value.nombreAtributo,
            value: value.nombreAtributoValor,
          }))
        );

        if (variantAttributes.length > 0) {
          const initialSelected: Record<string, string> = {};
          for (const attrVal of variantDetail.productoVarianteAtributoValores) {
            const attr = variantAttributes.find((attribute) => attribute.nombreAtributo === attrVal.nombreAtributo);
            if (!attr) continue;
            const value = attr.valores.find((item) => item.nombreValor === attrVal.nombreAtributoValor);
            if (!value) continue;
            initialSelected[attr.idProductoAtributo] = value.idProductoAtributoValor;
          }
          setSelectedAttributes(initialSelected);
        }
      } catch (error) {
        console.error("Error loading default variant", error);
      }
    };

    loadDefaultVariant();
  }, [productInfo.idProductoVarianteDefault, variantAttributes]);

  useEffect(() => {
    if (!variantAttributes || variantAttributes.length === 0) return;

    const selectedAttributeIds = Object.keys(selectedAttributes);
    if (selectedAttributeIds.length !== variantAttributes.length) {
      return;
    }

    const fetchVariant = async () => {
      try {
        const response = await HttpProduct.getProductVariantByAttributesValues(productInfo.id, {
          idsProductoAtributos: selectedAttributeIds,
          idsProductoAtributoValores: selectedAttributeIds.map((attrId) => selectedAttributes[attrId]),
        });

        if (response && response.length > 0) {
          const detail = await HttpProduct.getProductVariantDetail(response[0].id);
          setSelectedVariant(detail.productoVariante);
          setSelectedVariantAttributes(
            detail.productoVarianteAtributoValores.map((value) => ({
              name: value.nombreAtributo,
              value: value.nombreAtributoValor,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching variant:", error);
      }
    };

    fetchVariant();
  }, [selectedAttributes, variantAttributes, productInfo.id]);

  const handleAttributeChange = (attributeId: string, valueId: string) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [attributeId]: valueId,
    }));
  };

  const stock = selectedVariant?.stock ?? productInfo.stock ?? 0;
  const price = selectedVariant?.precio ?? productInfo.precio ?? 0;
  const imageForCart = selectedVariant?.urlImagenPrincipal || productInfo.urlImagenPrincipal;

  return (
    <>
      <main style={{ padding: "32px 32px 60px", position: "relative", zIndex: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 36 }}>
          <ImageCarousel
            images={productImages}
            productName={productInfo.nombre}
            productStock={stock}
            onImageClick={openFullscreenDialog}
            selectedImage={selectedImage}
            onImageSelect={setSelectedImage}
            onNextImage={nextImage}
            onPrevImage={prevImage}
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 20, position: "sticky", top: 100, alignSelf: "start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-500)", flexWrap: "wrap" }}>
              {breadcrumbs.map((breadcrumb, index) => (
                <React.Fragment key={`${breadcrumb.label}-${index}`}>
                  <span style={{ color: index === breadcrumbs.length - 1 ? "var(--ink-700)" : "var(--ink-500)" }}>
                    {breadcrumb.label}
                  </span>
                  {index < breadcrumbs.length - 1 && <span style={{ opacity: .5 }}>{">"}</span>}
                </React.Fragment>
              ))}
            </div>

            <ProductBasicInfo product={productInfo} variant={selectedVariant} />

            <ProductVariantSelector
              variantAttributes={variantAttributes}
              selectedAttributes={selectedAttributes}
              onChange={handleAttributeChange}
              selectedVariant={selectedVariant}
            />

            {stock > 0 ? (
              <QuantitySelector
                productId={productInfo.id.toString()}
                variantId={selectedVariant?.id}
                productName={productInfo.nombre}
                productPrice={price}
                productImage={imageForCart}
                quantity={quantity}
                stock={stock}
                onQuantityChange={handleQuantityChange}
                minQuantity={minimumPurchase}
                attributes={selectedVariantAttributes}
              />
            ) : (
              <button
                type="button"
                className="btn-primary"
                disabled
                style={{ width: "100%", opacity: .45, cursor: "not-allowed" }}
              >
                Sin stock
              </button>
            )}

            <ShippingCalculator
              onCalculateShipping={handleCalculateShipping}
              productId={productInfo.id.toString()}
              productVariantId={selectedVariant?.id}
              quantity={quantity}
            />
            <StoreInfo />
            <ProductDescription product={productInfo} />
          </div>
        </div>

        <RelatedProductsCarousel
          currentProductId={productInfo.id}
          categoryId={productInfo.categoriasIds ? productInfo.categoriasIds[0] : undefined}
        />
      </main>

      <FullscreenImageDialog
        open={fullscreenDialogOpen}
        onClose={closeFullscreenDialog}
        images={productImages}
        productName={productInfo.nombre}
        selectedImage={selectedImage}
      />
    </>
  );
}
